import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ChatMode = 'marketing' | 'ai-amp' | 'creator' | 'brand';

export type ChatPersona = 
  | 'platform-guide'
  | 'agent-scout' 
  | 'growth-guru'
  | 'dev-coach'
  | 'campaign-catalyst';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

// Site-aware configuration for Lumi
interface SiteConfig {
  greeting: string;
  suggestions: string[];
}

const SITE_CONFIG: Record<ChatMode, SiteConfig> = {
  'ai-amp': {
    greeting: "Hey! I'm Lumi. Looking for the perfect Ai agent or need help building one? I'm here to guide you.",
    suggestions: [
      "Find an agent for content repurposing",
      "How do I build my own agent?",
      "What are the most popular agents?"
    ]
  },
  'marketing': {
    greeting: "Hey! I'm Lumi, your lykbl guide. Whether you're a creator, brand, or just exploring - I'm here to help.",
    suggestions: [
      "What can lykbl do for creators?",
      "How do brand campaigns work?",
      "Show me how to get started"
    ]
  },
  'creator': {
    greeting: "Hey! I'm Lumi. Ready to grow your audience and supercharge your content? Let's do this!",
    suggestions: [
      "How can I increase engagement?",
      "What Ai agents can help me?",
      "Analyze my content strategy"
    ]
  },
  'brand': {
    greeting: "Hey! I'm Lumi. Let's find the perfect creators for your campaign and make it shine.",
    suggestions: [
      "Find creators for my campaign",
      "How does creator matching work?",
      "What makes a great brief?"
    ]
  }
};

export function getSiteConfig(mode: ChatMode): SiteConfig {
  return SITE_CONFIG[mode];
}

interface ChatContextType {
  mode: ChatMode;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  messages: ChatMessage[];
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, content: string) => void;
  clearMessages: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  persona: ChatPersona;
  setPersona: (persona: ChatPersona) => void;
  threadId: string | null;
  setThreadId: (id: string | null) => void;
  chatApiUrl: string;
  supabaseAnonKey: string;
}

const ChatContext = createContext<ChatContextType | null>(null);

// Get default persona based on mode
function getDefaultPersona(mode: ChatMode): ChatPersona {
  switch (mode) {
    case 'ai-amp':
      return 'agent-scout';
    case 'creator':
      return 'growth-guru';
    case 'brand':
      return 'campaign-catalyst';
    default:
      return 'platform-guide';
  }
}

// Get persona display info
export function getPersonaInfo(persona: ChatPersona) {
  const personas = {
    'platform-guide': {
      name: 'lykbl Guide',
      description: 'Your platform assistant',
      avatar: '🤖',
      greeting: "Hi! I'm your lykbl guide. How can I help you navigate the platform today?",
    },
    'agent-scout': {
      name: 'Agent Scout',
      description: 'Ai agent discovery specialist',
      avatar: '🔍',
      greeting: "Looking for the perfect Ai agent? Tell me what you're trying to accomplish and I'll help you find the right one!",
    },
    'growth-guru': {
      name: 'Growth Guru',
      description: 'Creator growth strategist',
      avatar: '📈',
      greeting: "Ready to grow your audience? I can analyze your performance and suggest strategies to level up your content game!",
    },
    'dev-coach': {
      name: 'Dev Coach',
      description: 'Developer success advisor',
      avatar: '💻',
      greeting: "Let's make your agents profitable! I can help with pricing, optimization, and marketplace best practices.",
    },
    'campaign-catalyst': {
      name: 'Campaign Catalyst',
      description: 'Campaign optimization expert',
      avatar: '🎯',
      greeting: "Need help with your campaign? I can help you find the right creators and optimize your strategy!",
    },
  };
  return personas[persona];
}

// Map persona to agent ID in the manifest registry
export function getAgentIdForPersona(persona: ChatPersona): string {
  const agentMap: Record<ChatPersona, string> = {
    'platform-guide': 'lykbl.platform-guide',
    'agent-scout': 'lykbl.agent-scout',
    'growth-guru': 'lykbl.growth-guru',
    'dev-coach': 'lykbl.dev-coach',
    'campaign-catalyst': 'lykbl.campaign-catalyst',
  };
  return agentMap[persona] || 'lykbl.platform-guide';
}

interface ChatProviderProps {
  children: ReactNode;
  mode: ChatMode;
  chatApiUrl?: string;
  supabaseAnonKey?: string;
  defaultPersona?: ChatPersona;
}

export function ChatProvider({ 
  children, 
  mode, 
  chatApiUrl = '/functions/v1/chat',
  supabaseAnonKey = '',
  defaultPersona,
}: ChatProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [persona, setPersona] = useState<ChatPersona>(
    defaultPersona || getDefaultPersona(mode)
  );

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const id = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      {
        ...message,
        id,
        timestamp: new Date(),
      },
    ]);
    return id;
  }, []);

  const updateMessage = useCallback((id: string, content: string) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === id ? { ...msg, content, isStreaming: false } : msg
      )
    );
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setThreadId(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        mode,
        isOpen,
        setIsOpen,
        toggle,
        messages,
        addMessage,
        updateMessage,
        clearMessages,
        isLoading,
        setIsLoading,
        persona,
        setPersona,
        threadId,
        setThreadId,
        chatApiUrl,
        supabaseAnonKey,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

